import { useEffect, useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import {
  getTeacher,
  getTeacherId,
  parseItemResponse,
} from "../../helper/adminUsersApi";

const DetailItem = ({ label, value }) => (
  <tr className="border-b last:border-b-0">
    <td className="p-4 text-gray-600 font-medium align-top">{label}</td>
    <td className="p-4 font-semibold text-gray-900">{value || "-"}</td>
  </tr>
);

function formatList(value) {
  if (Array.isArray(value) && value.length) return value.filter(Boolean).join(", ");
  return value || "-";
}

export default function ViewTeacherModel({ open, setOpen, teacher }) {
  const { t } = useTranslation();
  const [details, setDetails] = useState(teacher || {});

  useEffect(() => {
    if (!open) return undefined;
    setDetails(teacher || {});
    const teacherId = getTeacherId(teacher);
    if (!teacherId) return undefined;

    let cancelled = false;
    getTeacher(teacherId)
      .then((response) => {
        const item = parseItemResponse(response);
        if (!cancelled && item) setDetails(item);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, teacher]);

  const handleClose = () => {
    requestAnimationFrame(() => setOpen(false));
  };

  const fullName = `${details?.firstName || ""} ${details?.lastName || ""}`.trim();

  return (
    <Dialog
      open={open}
      as="div"
      className="relative z-40 focus:outline-none"
      onClose={handleClose}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full relative max-w-2xl rounded-2xl bg-gray-50 p-6">
            <IoMdClose
              className="absolute right-5 top-6 cursor-pointer text-gray-600 hover:text-gray-900"
              fontSize={24}
              onClick={handleClose}
            />

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6 border-b flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-600">
                  {details?.firstName ? details.firstName[0].toUpperCase() : "?"}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    {fullName || t("teacher_management.personal_info")}
                  </h1>
                  <p className="text-md text-gray-500">{details?.email || details?.username || ""}</p>
                </div>
              </div>

              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-700 mb-4">
                  {t("teacher_management.personal_info")}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <tbody>
                      <DetailItem
                        label={t("teacher_management.teacher_id")}
                        value={getTeacherId(details)}
                      />
                      <DetailItem label={t("username")} value={details?.username} />
                      <DetailItem label={t("first_name")} value={details?.firstName} />
                      <DetailItem label={t("last_name")} value={details?.lastName} />
                      <DetailItem label={t("email")} value={details?.email} />
                      <DetailItem
                        label={t("status")}
                        value={
                          details?.status ? (
                            <span
                              className={`px-2 py-1 text-sm rounded-full ${
                                String(details.status).toUpperCase() === "ACTIVE"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {details.status}
                            </span>
                          ) : (
                            "-"
                          )
                        }
                      />
                      <DetailItem
                        label={t("preferred_language")}
                        value={details?.preferredLanguage}
                      />
                      <DetailItem label={t("country_code")} value={details?.countryCode} />
                      <DetailItem
                        label={t("teacher_management.subjects")}
                        value={formatList(details?.subjectsToTeach)}
                      />
                      <DetailItem
                        label={t("teacher_management.schools")}
                        value={formatList(details?.schoolIds || details?.schools)}
                      />
                      <DetailItem
                        label={t("creation_date") || t("teacher_management.registration_date")}
                        value={
                          details?.creationDate
                            ? new Date(details.creationDate).toLocaleDateString()
                            : "-"
                        }
                      />
                      <DetailItem
                        label={t("teacher_management.roles")}
                        value={
                          details?.userRoles?.length ? (
                            <div className="flex flex-wrap gap-2">
                              {details.userRoles.map((role, index) => (
                                <span
                                  key={index}
                                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {String(role).replace("zubrus_", "")}
                                </span>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )
                        }
                      />
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
